import { Link } from "gatsby";
import { GatsbyImage, getImage, ImageDataLike } from "gatsby-plugin-image";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useElementSize } from "../../hooks/useElementSize";
import { useLoc } from "../../hooks/useLoc";
import { useMonth } from "../../hooks/useMonth";
import { monthSlugs } from "../../services/centralStore";
import ensureMonthIndexInRange from "../../services/ensureMonthIndexInRange";
import firstCapital from "../../services/firstCapitalCase";
import { getSeason } from "../../services/getSeason";
import { WeatherData } from "../../templates/fragments/weather-data";
import { MonthIndexChart } from "../search/map/month-index-chart";
import { ActivityTag } from "./card/activity-tag";
import { ContinentTag } from "./card/country-tag";
import { InfoSection } from "./card/info-section";

const icons = {
    nature: "ico_hiker.svg",
    beach: "ico_sun_white.svg",
    city: "ico_city.svg"
};

export type WeatherDataSimplified = {
    tmax: number[];
    sst: number;
    wet: number;
};

type DestinationCardProps = {
    imageDataLike: ImageDataLike;
    partOfTheWorld: string;
    tags?: string[];
    location: string;
    slug: string;
    originalSlug: string;
    weatherData: WeatherData;
    forceSmall?: boolean;
    forceVerySmall?: boolean;
    lazyLoad?: boolean;
    className?: string;
    countryName?: string;
    includeCountry?: boolean;
    monthIndexes?: number[];
    onHover?: (hovered: boolean, slug: string) => void;
};

export const DestinationCard = ({
    imageDataLike: backgroundImageSrc,
    partOfTheWorld,
    tags = [],
    location,
    weatherData,
    forceSmall,
    forceVerySmall,
    originalSlug,
    slug,
    lazyLoad,
    countryName,
    includeCountry,
    className = "",
    monthIndexes,
    onHover
}: DestinationCardProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const [immediatelyHovered, setImmediatelyHovered] = useState(false);
    const [additionalInfoHeight, setAdditionalInfoHeight] = useState(81);
    const windowWidth = useElementSize({ defaultValue: 600 });
    const additionalInfoRef = useRef(null);
    const loc = useLoc();
    const { month } = useMonth({ ignoreOverview: true });

    const monthIndex = monthSlugs.indexOf(month);

    let indexes = [monthIndex - 1, monthIndex + 1];
    indexes = indexes.map((idx) => ensureMonthIndexInRange(idx));
    const [lastMonthIndex, nextMonthIndex] = indexes;
    const [lastMonth, nextMonth] = [
        monthSlugs[lastMonthIndex],
        monthSlugs[nextMonthIndex]
    ];
    const [lastMonthTemperature, thisMonthTemperature, nextMonthTemperature] = [
        weatherData.tmax[lastMonthIndex],
        weatherData.tmax[monthIndex],
        weatherData.tmax[nextMonthIndex]
    ];
    const seaTemperature = weatherData.sst[monthIndex];
    const numberOfRainyDays = weatherData.wet[monthIndex];

    const image = getImage(backgroundImageSrc);
    useEffect(() => {
        if (additionalInfoRef?.current) {
            setAdditionalInfoHeight(additionalInfoRef.current.offsetHeight);
        }
    }, []);

    let hoverTimeout = null;

    const hoverChanged = (newState: boolean) => {
        if (onHover && newState != immediatelyHovered) {
            onHover(newState, originalSlug);
            setImmediatelyHovered(newState);
        }

        if (hoverTimeout) clearTimeout(hoverTimeout);
        hoverTimeout = setTimeout(
            () => {
                if (!forceSmall) {
                    setIsHovered(newState);
                }
            },
            newState ? 100 : 0
        );
    };

    useEffect(() => {
        if (hoverTimeout) {
            clearTimeout(hoverTimeout);
        }
        return () => {
            if (hoverTimeout) {
                clearTimeout(hoverTimeout);
            }
        };
    });

    const canHoverMove = isHovered && windowWidth >= 768;

    return (
        <div
            className={`relative rounded-lg ${
                forceVerySmall
                    ? "h-[180px] min-h-[180px] w-[180px] min-w-[180px]"
                    : "h-[200px] min-h-[200px] w-[320px] min-w-[320px]"
            } ${!forceSmall && "sm:h-[320px]"} ${
                immediatelyHovered ? "shadow-hovered" : "shadow-hovered-hidden"
            } overflow-hidden ${className}`}
            onMouseEnter={() => hoverChanged(true)}
            onMouseMove={() => hoverChanged(true)}
            onMouseLeave={() => hoverChanged(false)}
        >
            <Link to={slug}>
                <GatsbyImage
                    loading={lazyLoad ? "lazy" : "eager"}
                    alt={""}
                    image={image}
                    className={"absolute -z-30 h-full w-full rounded-lg"}
                />

                {/* tags */}
                <div
                    className={`absolute ${
                        forceSmall || forceVerySmall
                            ? "top-[8px]"
                            : "top-[12px]"
                    } left-[12px] right-[12px] flex ${
                        forceVerySmall && "hidden"
                    } ${
                        !forceSmall && "sm:flex-col sm:gap-[12px]"
                    } flex-wrap items-start gap-[8px]`}
                >
                    <ContinentTag locationName={partOfTheWorld} />
                    {includeCountry && (
                        <ContinentTag locationName={countryName} />
                    )}
                    {tags.map((a) => (
                        <ActivityTag
                            key={a}
                            className=""
                            activityIconSrc={`/${icons[a]}`}
                            nameOfActivity={loc(`common.activities.${a}`)}
                        />
                    ))}
                </div>

                {/* darken overlay */}
                <div
                    className={`card-fade absolute top-0 h-full w-full ${
                        !forceSmall && "sm:card-fade-big"
                    } -z-20 rounded-lg transition-opacity duration-500`}
                    style={{ opacity: canHoverMove ? 1 : 0.4 }}
                />

                {/* on hover movable */}
                <div
                    className={`pointer-events-none absolute -z-10 w-full text-white transition-[bottom] duration-500`}
                    style={{
                        bottom: canHoverMove ? 0 : -1 * additionalInfoHeight
                    }}
                >
                    <div
                        className={`flex w-full justify-center text-center font-bold tracking-wide ${
                            forceVerySmall ? "px-[8px]" : "px-[16px]"
                        } leading-tight ${
                            forceVerySmall && location.length >= 30
                                ? "text-[20px] "
                                : "text-[24px]"
                        }`}
                    >
                        {location}
                    </div>
                    {!monthIndexes && (
                        <div className="w-full">
                            <div
                                className={`mb-[4px] flex w-full justify-center py-[10px] px-[10px]`}
                            >
                                <InfoSection
                                    className={`basis-1/3`}
                                    amount={thisMonthTemperature}
                                    sectionTitle={loc("common.months." + month)}
                                    isTemperature={true}
                                    season={getSeason({
                                        wet: weatherData.wet[monthIndex]
                                    })}
                                    smallMargins={forceSmall || forceVerySmall}
                                />
                                {weatherData.sst[monthIndex] ? (
                                    <InfoSection
                                        className={`basis-1/3 ${
                                            forceVerySmall && "hidden"
                                        }`}
                                        amount={seaTemperature}
                                        sectionTitle={firstCapital(
                                            loc("common.sea")
                                        )}
                                        isSeaTemperature={true}
                                        smallMargins={forceSmall}
                                    />
                                ) : (
                                    <span />
                                )}
                                <InfoSection
                                    className={`basis-1/3  ${
                                        forceVerySmall && "hidden"
                                    }`}
                                    amount={numberOfRainyDays}
                                    sectionTitle={firstCapital(
                                        loc("common.rainyDays")
                                    )}
                                    isNumberOfDays={true}
                                    smallMargins={forceSmall}
                                />
                            </div>
                            <div
                                ref={additionalInfoRef}
                                className={`flex w-full bg-white bg-opacity-30 px-[10px] pt-[10px] pb-[14px]
                            ${
                                canHoverMove
                                    ? "before:absolute before:mt-[-18px] before:ml-[24px] before:border-x-[6.5px] before:border-t-0 before:border-b-[8px] before:border-solid before:border-x-transparent before:border-b-white before:border-t-transparent before:border-opacity-30"
                                    : ""
                            }`}
                            >
                                <InfoSection
                                    className="basis-1/3"
                                    amount={lastMonthTemperature}
                                    sectionTitle={loc(
                                        "common.months." + lastMonth
                                    )}
                                    isTemperature={true}
                                    season={getSeason({
                                        wet: weatherData.wet[lastMonthIndex]
                                    })}
                                />
                                <InfoSection
                                    className="basis-1/3"
                                    amount={thisMonthTemperature}
                                    sectionTitle={loc("common.months." + month)}
                                    isTemperature={true}
                                    season={getSeason({
                                        wet: weatherData.wet[monthIndex]
                                    })}
                                />
                                <InfoSection
                                    className="basis-1/3"
                                    amount={nextMonthTemperature}
                                    sectionTitle={loc(
                                        "common.months." + nextMonth
                                    )}
                                    isTemperature={true}
                                    season={getSeason({
                                        wet: weatherData.wet[nextMonthIndex]
                                    })}
                                />
                            </div>
                        </div>
                    )}
                    {monthIndexes && (
                        <div className="mt-[20px] h-[136px] px-[20px] text-[18px] font-bold text-white">
                            <MonthIndexChart
                                monthIndexes={monthIndexes}
                                className="mt-[8px] w-full"
                            />
                        </div>
                    )}
                </div>

                <div
                    className={`absolute top-0 z-10 h-full w-full rounded-lg bg-opacity-0`}
                />
            </Link>
        </div>
    );
};
